import { Box, Divider, Grid, Stack } from '@mui/material';
import Container from '@mui/material/Container';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'react-string-format';
import { constants } from '../../constants';
import useMyMApi from '../../hooks/useMyMApi';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton, MyMCloseButton } from '../../ui-elements/MyMButton';
import { MyMImage } from '../../ui-elements/MyMImage';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { MyMModal } from '../../ui-elements/MyMModal';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import CustomAccordion from '../../ui-parts/CustomAccordion';
import { utility } from '../../utility';
import { SurveyResultDownlodResultButton } from './SurveyResultDownlodResultButton';
import { SurveyResultOptionBarGraph, SurveyResultOptionPieGraph, SurveyResultOptionPieGraphLegend } from './SurveyResultGraph';

/** MyMine Version2.0 SurveyResult画面追加
 * SurveyResult Page Component
 */
const SurveyResult = () => {

  const screenId = 'MyM802';
  const apiRequest = useMyMApi(screenId);
  const { controlSettings, screenSettings } = useWebContext(screenId);
  const [surveySummaryInfo, setSurveySummaryInfo] = useState(null);
  const navigate = useNavigate();
  const queryParam = useParams();
  const [modalQuestionInfo, setModalQuestionInfo] = useState(null);

  useEffect(() => {

    /**
     * SurveySummary情報取得成功時処理
     * @param {any} result
     */
    const callbackSuccessRequestSurveySummary = (result) => {
      setSurveySummaryInfo(result);
    }

    /**
     * SurveySummary情報取得失敗時処理
     */
    const callbackFailedRequestSurveySummary = () => {
      // エラーページ遷移
      navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
    }

    // 表示情報取得
    apiRequest.requestJson(`api/survey/summary?surveyId=${queryParam.survey_id}`, 'GET', callbackSuccessRequestSurveySummary, callbackFailedRequestSurveySummary)

  }, []);

  if (surveySummaryInfo === null)
    return <MyMProgress />

  return (
    <Container className='d-flex justify-content-center flex-column h-100'>
      <Stack direction='column' spacing={2} className='pb-3 h-100'>
        {/* ヘッダ */}
        <Stack alignItems='center' sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          <MyMImage
            screenId={screenId}
            filePath={controlSettings.ImageList.imgMyMineLogo}
            alt='Logo'
            style={{ width: '350px', height: '110px' }}
            display='block'
          />
          <MyMVariantLabel variant='h6'>{controlSettings.LabelList.lblHeader}</MyMVariantLabel>
        </Stack>
        <Grid container rowSpacing={2} sx={{ '>.MuiGrid-item': { paddingTop: 0 } }}>
          <Grid item xs={12} md={8}>
            <Grid container rowSpacing={2}>
              <Grid item xs={5} md={2}><MyMLabel>{controlSettings.LabelList.lblTerm}</MyMLabel></Grid>
              <Grid item xs={7} md={10}><MyMLabel>{`${utility.formatDate(surveySummaryInfo.StartDate)} ～ ${utility.formatDate(surveySummaryInfo.EndDate)}`}</MyMLabel></Grid>
              <Grid item xs={8} md={2}><MyMLabel>{controlSettings.LabelList.lblAnsweredRatio}</MyMLabel></Grid>
              <Grid item xs={4} md={10} sx={{ paddingBottom: { xs: '25px', md: 0 } }}>
                <MyMLabel>
                  {`${surveySummaryInfo.AnswerUserCount} / ${surveySummaryInfo.UserCount} (${surveySummaryInfo.UserCount > 0 ? Math.round(surveySummaryInfo.AnswerUserCount / surveySummaryInfo.UserCount * 100) : 0}%)`}
                </MyMLabel>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4} className='d-flex justify-content-end align-items-end'>
            {surveySummaryInfo.IsReferOnlyOptionQuestion === false && <SurveyResultDownlodResultButton screenId={screenId} surveyId={queryParam.survey_id} surveySummaryInfo={surveySummaryInfo} />}
          </Grid>
        </Grid>
        {/* Summary Result */}
        <Box sx={{ '.MuiAccordionSummary-content > p': { width: '100%' } }}>
          {surveySummaryInfo.QuestionList
            .filter(x => x.AnswerType === constants.SurveyAnswerType.Option || x.AnswerType === constants.SurveyAnswerType.Radiobutton || x.AnswerType === constants.SurveyAnswerType.Checkbox)
            .map(x => {
              let answerResultList = surveySummaryInfo.AnswerResultList.filter(y => y.QuestionId === x.QuestionId);
              // 2025.7.7 MyMine Version3.0対応 Add Start
              // Mutiple Selection, Single Selectoionの場合、未回答データを除外
              if (x.AnswerType === constants.SurveyAnswerType.Radiobutton || x.AnswerType === constants.SurveyAnswerType.Checkbox) {
                answerResultList = answerResultList.filter(x => x.AnswerSelectOptionList !== null);
              }
              // 2025.7.7 MyMine Version3.0対応 Add End
              return (
                <CustomAccordion
                  key={x.QuestionId}
                  menuText={
                    // 2025.7.7 MyMine Version3.0対応 Modify Start
                    <span style={{ width: '100%', display: 'flex' }}>
                      <span style={{ flexGrow: 1, display: 'flex', alignItems: 'center', }}>
                        {`${x.OrderNo}. ${x.QuestionText.replace(/\n/g, ' ')}`}
                        {x.RequiredFlg && <span style={{ display: 'contents', color: 'red', fontSize: 14, fontWeight: 'normal', }}>{controlSettings.LabelList.lblRequired}</span>}
                      </span>
                      <span style={{ width: 110, minWidth: 110 }}>
                        {controlSettings.LabelList.lblAnswerTypeText}
                        <span style={{ marginBottom: 0, display: 'block', fontWeight: 'normal' }}>
                          {x.AnswerType === constants.SurveyAnswerType.Option ? controlSettings.LabelList.lblScaleType :
                            x.AnswerType === constants.SurveyAnswerType.Radiobutton ? controlSettings.LabelList.lblSingleSelectType :
                              x.AnswerType === constants.SurveyAnswerType.Checkbox ? controlSettings.LabelList.lblMultiSelectType : ''}
                        </span>
                      </span>
                    </span>
                    // 2025.7.7 MyMine Version3.0対応 Modify End
                  }
                  contents={
                    <Stack bgcolor='#fff'>
                      <MyMLabel className='fw-bold' sx={{ paddingLeft: 3, paddingTop: 1 }}>
                        {controlSettings.LabelList.lblAllUser}
                        {/* 2025.7.7 MyMine Version3.0対応 Add Start */}
                        <span style={{ marginLeft: 20, fontWeight: 'normal' }}>
                          {controlSettings.LabelList.lblTotalAnswerText} {answerResultList.filter(x => (x.AnswerSelectOptionList ?? x.AnswerOption) !== null).length}
                        </span>
                        {/* 2025.7.7 MyMine Version3.0対応 Add End */}
                      </MyMLabel>

                      {/* 棒グラフ */}
                      <SurveyResultOptionBarGraph
                        controlSettings={controlSettings}
                        answerResultList={answerResultList}
                        // 2025.7.7 MyMine Version3.0対応 Add Start
                        screenSettings={screenSettings}
                        optionList={surveySummaryInfo.QuestionOptionList[x.QuestionId.toString()]}
                        // 2025.7.7 MyMine Version3.0対応 Add End
                      />

                      <Divider sx={{ borderColor: 'black' }} />
                      <Box sx={{ paddingLeft: 3, paddingTop: 1 }}>
                        <Box className='d-flex justify-content-between align-items-center ps-3'>
                          <MyMLabel className='fw-bold'>{controlSettings.LabelList.lblByRegion}</MyMLabel>
                        </Box>

                        {/* 凡例 */}
                        <Box sx={{ float: { md: 'right' }, marginTop: { md: '-1rem' }, marginLeft: '80px' }} className='ps-3'>
                          <SurveyResultOptionPieGraphLegend
                            // 2025.7.7 MyMine Version3.0対応 Add Start
                            optionList={surveySummaryInfo.QuestionOptionList[x.QuestionId.toString()]}
                            // 2025.7.7 MyMine Version3.0対応 Add End
                          />
                        </Box>


                        {/* 円グラフ */}
                        <Grid container sx={{ paddingLeft: 2, paddingTop: 2 }} rowSpacing={2}>
                          {surveySummaryInfo.CompanyHierarchyList
                            .filter(company => company.IsRegion)
                            .map(company =>
                              <Grid item xs={12} sm={6} md={4} lg={3} key={company.Id}>
                                <MyMLabel className='fw-bold'>{company.Name}</MyMLabel>
                                <SurveyResultOptionPieGraph
                                  controlSettings={controlSettings}
                                  onClickGraph={surveySummaryInfo.CompanyHierarchyList.some(company2 => company2.ParentId === company.Id) ? () => setModalQuestionInfo({ RegionCompanyId: company.Id, QuestionId: x.QuestionId }) : null}
                                  answerResultList={answerResultList.filter(ans => ans.CompanyId === company.Id || ans.ParentCompanyId === company.Id)}
                                  // 2025.7.7 MyMine Version3.0対応 Add Start
                                  optionList={surveySummaryInfo.QuestionOptionList[x.QuestionId.toString()]}
                                  // 2025.7.7 MyMine Version3.0対応 Add End
                                />
                              </Grid>
                            )}
                        </Grid>
                      </Box>
                    </Stack>}
                />)
            })}
        </Box>

        <Stack direction='row' justifyContent="flex-end" spacing={3} className='flex-fill align-items-end  pb-2' >
          <MyMCloseButton className='ms-auto' closeText={controlSettings.LabelList.btnClose}></MyMCloseButton>
        </Stack>
      </Stack>

      <MyMModal
        open={modalQuestionInfo !== null}
        onClose={() => setModalQuestionInfo(null)}
        height='80%'
        width={{xs:'90%',sm:'85%',lg:'65%' }}
      >
        <Box className='h-100 d-flex flex-column' >
          <Box className='d-flex justify-content-between ps-3'>
            <MyMLabel className='fw-bold' sx={{ minWidth: { xs: 100, md: 250 } }} >
              {format(controlSettings.LabelList.lblByCompany, surveySummaryInfo.CompanyHierarchyList.find(company => company.Id === modalQuestionInfo?.RegionCompanyId)?.Name)}
            </MyMLabel>
            <SurveyResultOptionPieGraphLegend optionList={surveySummaryInfo.QuestionOptionList[modalQuestionInfo?.QuestionId.toString()]} />
          </Box>
          <Grid container sx={{ paddingLeft: 2, paddingTop: 2 }} rowSpacing={2}>
            {surveySummaryInfo.CompanyHierarchyList
              .filter(company => company.ParentId === modalQuestionInfo?.RegionCompanyId || company.Id === modalQuestionInfo?.RegionCompanyId)
              .map(company => {
                // 2025.7.7 MyMine Version3.0対応 Add Start
                let answerResultList = surveySummaryInfo.AnswerResultList.filter(ans => ans.QuestionId === modalQuestionInfo?.QuestionId && ans.CompanyId === company.Id);
                // Mutiple Selection, Single Selectoionの場合、未回答データを除外
                const answerType = surveySummaryInfo.QuestionList.find(x => x.QuestionId === modalQuestionInfo?.QuestionId).AnswerType;
                if (answerType === constants.SurveyAnswerType.Radiobutton || answerType === constants.SurveyAnswerType.Checkbox) {
                  answerResultList = answerResultList.filter(x => x.AnswerSelectOptionList !== null);
                }
                // 2025.7.7 MyMine Version3.0対応 Add End
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={company.Id}>
                    <MyMLabel className='fw-bold'>{company.Name}</MyMLabel>
                    <SurveyResultOptionPieGraph
                      controlSettings={controlSettings}
                      // 2025.7.7 MyMine Version3.0対応 Modify Start
                      answerResultList={answerResultList}
                      optionList={surveySummaryInfo.QuestionOptionList[modalQuestionInfo?.QuestionId.toString()]}
                      // 2025.7.7 MyMine Version3.0対応 Modify End
                    />
                  </Grid>)
              }
              )}
          </Grid>
          <Stack direction='row' justifyContent="flex-end" spacing={3} className='flex-fill align-items-end pb-2'>
            <MyMButton onClick={() => setModalQuestionInfo(null)}>{controlSettings.LabelList.btnClose}</MyMButton>
          </Stack>
        </Box>
      </MyMModal>
    </Container >
  );
}
export default SurveyResult;
